import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useCreateEntity } from 'src/features/entity/api/useCreateEntity';
import { useGetMembership } from 'src/features/membership/api/useGetMembership';
import MembershipForm from 'src/features/membership/components/forms/MembershipForm';
import { membershipFormSchema, MembershipFormSchemaValues } from 'src/features/membership/formSchema';
import { Membership } from 'src/features/membership/types';
import { useListMembershipTypes } from 'src/features/membershipType/api/useListMembershipTypes';
import { Translation } from 'src/types';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { SmartParkError } from 'src/registry/types/axios';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'src/registry/ui/DropdownMenu';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';
import { cn } from 'src/utils/shadcn';
import { useParking } from 'src/providers/ParkingProvider';

interface MembershipFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedMembership?: Membership;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: MembershipFormSchemaValues,
        onSuccess: () => void,
        onError: (error: AxiosError<SmartParkError, any>) => void,
    ) => void;
}

const translate = (translations: Translation[], locale: string) =>
    translations.find((translation) => translation.language === locale);

function MembershipFormModal<TData>({
    selectedMembership,
    className,
    isPending,
    children,
    icon,
    title,
    subtitle,
    mutate,
}: MembershipFormModalProps<TData>) {
    const { locale } = useIntl();
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [membershipType, setMembershipType] = React.useState({
        id: selectedMembership?.membership_type.id ?? '',
        name: translate(selectedMembership?.membership_type.translations ?? [], locale)?.name ?? '',
        duration: selectedMembership?.membership_type.duration ?? null,
        durationUnit: selectedMembership?.membership_type.duration_unit ?? null,
        max_allowed_spaces_count: selectedMembership?.max_allowed_spaces_count ?? null,
    });

    const { parkingId } = useParking();
    const { category = '' } = useParams();

    const { data: membership } = useGetMembership({
        parkingId,
        membershipId: selectedMembership?.id ?? '',
        // We only want to fetch if we're in edit
        config: { enabled: !!selectedMembership?.id && isModalOpen },
    });

    const { data: membershipTypes } = useListMembershipTypes({
        parkingId,
        category,
        is_active: true,
        config: { enabled: !selectedMembership },
    });

    const { mutate: createEntity } = useCreateEntity({});

    const onError = useOnError();

    const defaultValues = React.useMemo(
        () =>
            selectedMembership
                ? {
                      ...selectedMembership,
                      membership_type: selectedMembership.membership_type.id,
                      owner: selectedMembership.owner?.id,
                  }
                : {
                      membership_type: membershipType.id,
                      max_allowed_spaces_count: membershipType.max_allowed_spaces_count,
                  },
        [membershipType, selectedMembership],
    );

    const form = useForm<MembershipFormSchemaValues>({
        resolver: zodResolver(membershipFormSchema),
        defaultValues,
    });

    // defaultValues are cached and require the reset API to update
    React.useEffect(() => {
        form.reset(defaultValues);
    }, [membership, isModalOpen, category, form, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: MembershipFormSchemaValues) => {
            if (values.entity) {
                createEntity(
                    { parkingId, ...values.entity },
                    {
                        onSuccess: (entity) => {
                            const newValues = {
                                ...values,
                                owner: entity.id,
                            };
                            mutate(
                                newValues,
                                () => setIsModalOpen(false),
                                (error) => onError(error, form.setError),
                            );
                        },
                    },
                );
            } else
                mutate(
                    values,
                    () => setIsModalOpen(false),
                    (error) => onError(error, form.setError),
                );
        },
        [parkingId, mutate, createEntity, onError, form.setError],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button className={cn('ml-auto', className)}>
                                <FontAwesomeIcon icon={ICON_MAP.ADD} />
                                {title}
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-50">
                            {membershipTypes?.results.map((option) => {
                                const name = translate(option.translations, locale)?.name ?? '';
                                return (
                                    <DialogTrigger asChild key={option.id}>
                                        <DropdownMenuItem
                                            onClick={() =>
                                                setMembershipType({
                                                    name,
                                                    id: option.id,
                                                    duration: option.duration,
                                                    durationUnit: option.duration_unit,
                                                    max_allowed_spaces_count: option?.max_allowed_spaces_count ?? null,
                                                })
                                            }
                                        >
                                            {name}
                                        </DropdownMenuItem>
                                    </DialogTrigger>
                                );
                            })}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </>
            )}
            <DialogContent>
                <DialogHeader className="flex-row gap-2">
                    <SquareIcon icon={icon} />
                    <div>
                        <DialogTitle>{title}</DialogTitle>
                        <DialogDescription>{subtitle}</DialogDescription>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <MembershipForm form={form} onSubmit={onSubmit} membershipTypeData={membershipType} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage
                                id={generalMessages.cancel.id}
                                defaultMessage={generalMessages.cancel.defaultMessage}
                            />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedMembership && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default MembershipFormModal;
